import { z } from "zod"
import { ProductSchema } from "../pos"
import { Discount, DiscountSchema } from "./discount"
import {
  ElectricBikeRequirement,
  ProductChoiceSchema,
  ServiceAction,
  ServiceBikeGearSystem,
  ServiceBikeType,
} from "./index"
import { PackageLeasingRule } from "./package-leasing-rule"
import { ServiceWashWithoutBookabilitySchema } from "./service-wash"
import { ServiceWashWithoutBookability } from "./service-wash"

export const ServicePackageItemSchema = z.object({
  partId: z.guid(),
  partName: z.string(),
  partImage: z.string(),
  includedActions: z.array(z.nativeEnum(ServiceAction)),
})

export const ServicePackageItemSnapshotSchema = ServicePackageItemSchema.omit({
  includedActions: true,
}).extend({
  comment: z.string().nullable(),
  action: z.nativeEnum(ServiceAction),
  products: z.array(ProductSchema),
  productChoices: z.array(ProductChoiceSchema),
})

export type ServicePackageItem = z.infer<typeof ServicePackageItemSchema>
export type ServicePackageItemSnapshot = z.infer<
  typeof ServicePackageItemSnapshotSchema
>

export const ServicePackagePriceEntrySchema = z.object({
  id: z.guid().optional(),
  price: z.number().positive(),
  includedMaterialCost: z.number().int().nonnegative().nullable().default(null),
  /** Estimated minutes of work for a bike this entry applies to. */
  laborTimeMinutes: z.number().int().nonnegative().nullable().default(null),
  electricRequirement: z
    .nativeEnum(ElectricBikeRequirement)
    .default(ElectricBikeRequirement.NO_REQUIREMENT),
  requiresClubMembership: z.boolean().default(false),
  onlyApplicableToBikeTypes: z.array(z.nativeEnum(ServiceBikeType)).default([]),
  onlyApplicableToCustomBikeTypes: z.array(z.guid()).default([]),
  onlyApplicableToGearSystems: z
    .array(z.nativeEnum(ServiceBikeGearSystem))
    .default([]),
  priceListIds: z.array(z.guid()).default([]),
})

export type ServicePackagePriceEntry = z.infer<
  typeof ServicePackagePriceEntrySchema
>

/**
 * A price entry as sent on create/update. The included material comes off the
 * package labor, so it can never be more than the package price.
 */
export const ServicePackagePriceEntryInputSchema =
  ServicePackagePriceEntrySchema.omit({ id: true }).refine(
    ({ includedMaterialCost, price }) =>
      includedMaterialCost == null || includedMaterialCost <= price,
    {
      message: "Included material cost cannot exceed the price",
      path: ["includedMaterialCost"],
    },
  )

export const ServicePackageSchema = z.object({
  id: z.guid(),
  name: z.string().min(1),
  description: z.string().nullable(),
  wash: ServiceWashWithoutBookabilitySchema.nullable(),
  isBookable: z.boolean(),
  requiresManualActivation: z.boolean(),
  leasingRule: z.nativeEnum(PackageLeasingRule),
  /** The companies ONLY_FOR or EXCLUDED_FOR applies to; empty for ALL. */
  leasingCompanyIds: z.array(z.guid()),
  items: z.array(ServicePackageItemSchema),
  priceEntries: z.array(ServicePackagePriceEntrySchema),
  displayOrder: z.number().default(0),
  defaultDiscount: DiscountSchema.nullable(),
})

export const CreateServicePackageSchema = ServicePackageSchema.omit({
  id: true,
  wash: true,
  defaultDiscount: true,
}).extend({
  items: z.array(ServicePackageItemSchema.omit({ partName: true })),
  priceEntries: z
    .array(ServicePackagePriceEntryInputSchema)
    .min(1, "At least one price entry is required"),
  washId: z.guid().nullable(),
  displayOrder: z.number().optional().default(0),
  requiresManualActivation: z.boolean().default(false),
  leasingRule: z.nativeEnum(PackageLeasingRule).default(PackageLeasingRule.ALL),
  leasingCompanyIds: z.array(z.guid()).default([]),
  defaultDiscount: DiscountSchema.nullable().optional(),
})

export const UpdateServicePackageSchema = CreateServicePackageSchema.omit({
  priceEntries: true,
})
  .partial()
  // Zod 4 applies .default() to missing keys even after .partial(), so the
  // defaulted fields are made plain optional again: a PATCH that leaves them
  // out must not reset them.
  .extend({
    displayOrder: z.number().optional(),
    requiresManualActivation: z.boolean().optional(),
    leasingRule: z.nativeEnum(PackageLeasingRule).optional(),
    leasingCompanyIds: z.array(z.guid()).optional(),
    priceEntries: z
      .array(ServicePackagePriceEntryInputSchema)
      .min(1, "At least one price entry is required")
      .optional(),
  })

export interface ServicePackage {
  id: string
  name: string
  description: string | null
  wash: ServiceWashWithoutBookability | null
  isBookable: boolean
  requiresManualActivation: boolean
  leasingRule: PackageLeasingRule
  leasingCompanyIds: string[]
  items: ServicePackageItem[]
  priceEntries: ServicePackagePriceEntry[]
  displayOrder: number
  defaultDiscount: Discount | null
}

export interface ServicePackageSnapshot {
  snapshotPackageId: string
  originalPackageId: string
  name: string
  description: string | null
  price: number
  includedMaterialCost: number | null
  laborTimeMinutes: number | null
  priceDiscount: Discount | null
  priceAfterDiscount: number | null
  wash: ServiceWashWithoutBookability | null
  isBookable: boolean
  electricRequirement: ElectricBikeRequirement
  requiresClubMembership: boolean
  onlyApplicableToBikeTypes: ServiceBikeType[]
  onlyApplicableToCustomBikeTypes: string[]
  onlyApplicableToGearSystems: ServiceBikeGearSystem[]
  items: ServicePackageItemSnapshot[]
  displayOrder: number
}

export const AddItemToServicePackageSchema = z.object({
  partId: z.guid(),
  action: z.nativeEnum(ServiceAction),
})

export const SetWashInServicePackageSchema = z.object({
  washId: z.guid().nullable(),
})

export type CreateServicePackage = z.infer<typeof CreateServicePackageSchema>
export type UpdateServicePackage = z.infer<typeof UpdateServicePackageSchema>
export type AddItemToServicePackage = z.infer<
  typeof AddItemToServicePackageSchema
>
